import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/constants';
import { IS_COMMUNITY_ENABLED } from '@/configs/community';
import {
  type ChurchProductsMap,
  hasCommunityAccess,
  hasCommunityListScopeType,
  hasGroupScopeType,
  isLimitedAdminScope,
  resolveAdminProductRedirectTarget,
} from '@/helpers';
import { useAdminProductContext, useGlobalSettings } from '@/hooks';
import churchesService from '@/services/churches.service';
import AuthService from '@/services/auth.service';
import { Links, ROUTE_ALIASES } from '@/routing/routesAliases';
import { CHURCH_PRODUCT } from '@/types';

export const AdminProductContextRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentChurch } = useGlobalSettings();
  const hasAppliedRedirectRef = useRef(false);
  const initialPathnameRef = useRef(location.pathname);
  const isLimitedScope = isLimitedAdminScope({
    isLimitedScope: currentChurch?.isLimitedScope,
    isGroupScope: currentChurch?.isGroupScope,
  });
  const isCommunityListOnlyScope =
    isLimitedScope &&
    hasCommunityListScopeType({ scopeTypes: currentChurch?.scopeTypes }) &&
    !hasGroupScopeType({
      scopeTypes: currentChurch?.scopeTypes,
      isGroupScope: currentChurch?.isGroupScope,
    });
  const isGroupLimitedScope =
    isLimitedScope &&
    hasGroupScopeType({
      scopeTypes: currentChurch?.scopeTypes,
      isGroupScope: currentChurch?.isGroupScope,
    });
  const isOnHomePath = location.pathname === ROUTE_ALIASES.HOME || location.pathname === '/';

  const { data: adminProductContext, isSuccess } = useAdminProductContext();

  const {
    data: churchProducts,
    isPending: isChurchProductsPending,
    isError: isChurchProductsError,
  } = useQuery({
    queryKey: [QUERY_KEYS.CHURCH_PRODUCTS, currentChurch?.churchId],
    queryFn: () => churchesService.getChurchesProducts(currentChurch?.churchId || ''),
    enabled: Boolean(currentChurch?.churchId),
    retry: false,
  });

  useEffect(() => {
    if (!currentChurch?.churchId) {
      return;
    }

    if (
      isOnHomePath &&
      isCommunityListOnlyScope &&
      IS_COMMUNITY_ENABLED &&
      hasCommunityAccess({ scopeTypes: currentChurch.scopeTypes })
    ) {
      navigate(Links.getCommunityRoute(), { replace: true });
      return;
    }

    const isOnMinistryGroupPath =
      location.pathname === ROUTE_ALIASES.MINISTRIES_GROUP ||
      location.pathname.startsWith(`${ROUTE_ALIASES.MINISTRIES_GROUP}/`);
    const isOnMpaGroupsPath =
      location.pathname === ROUTE_ALIASES.GROUPS || location.pathname.startsWith(`${ROUTE_ALIASES.GROUPS}/`);
    const isMinistriesEnabled =
      !isChurchProductsError && churchProducts?.[CHURCH_PRODUCT.MY_PARISH_MINISTRIES]?.isEnabled === true;
    const isMpaEnabled = !isChurchProductsError && churchProducts?.[CHURCH_PRODUCT.MPA]?.isEnabled === true;

    if (isGroupLimitedScope && isOnMinistryGroupPath) {
      return;
    }

    if (isOnHomePath && isGroupLimitedScope) {
      if (isChurchProductsPending) {
        return;
      }

      if (isMinistriesEnabled) {
        navigate(ROUTE_ALIASES.MINISTRIES_GROUP, { replace: true });
        return;
      }

      navigate(ROUTE_ALIASES.GROUPS, { replace: true });
      return;
    }

    if (isGroupLimitedScope && isOnMpaGroupsPath) {
      if (isChurchProductsPending) {
        return;
      }

      if (isMinistriesEnabled && !isMpaEnabled) {
        navigate(ROUTE_ALIASES.MINISTRIES_GROUP, { replace: true });
        return;
      }
    }

    if (isLimitedScope) {
      return;
    }

    if (isChurchProductsPending) {
      return;
    }

    if (hasAppliedRedirectRef.current) {
      return;
    }

    if (!AuthService.consumeAdminProductRedirectIntent()) {
      hasAppliedRedirectRef.current = true;
      return;
    }

    // Do not override explicit user navigation while async context is still loading.
    if (location.pathname !== initialPathnameRef.current) {
      hasAppliedRedirectRef.current = true;
      return;
    }

    if (isChurchProductsError) {
      hasAppliedRedirectRef.current = true;
      navigate(ROUTE_ALIASES.PROFILE, { replace: true });
      return;
    }

    const normalizedChurchProducts = (churchProducts ?? {}) as ChurchProductsMap;
    const hasEnabledProducts = Object.values(normalizedChurchProducts).some((product) => product?.isEnabled);
    if (!hasEnabledProducts) {
      hasAppliedRedirectRef.current = true;
      navigate(ROUTE_ALIASES.PROFILE, { replace: true });
      return;
    }

    const savedProduct = isSuccess && adminProductContext?.product ? adminProductContext.product : undefined;

    const target = resolveAdminProductRedirectTarget({
      savedProduct,
      churchProducts: normalizedChurchProducts,
    });

    if (target.kind === 'none') {
      hasAppliedRedirectRef.current = true;
      navigate(ROUTE_ALIASES.PROFILE, { replace: true });
      return;
    }

    hasAppliedRedirectRef.current = true;

    if (target.kind === 'internal') {
      if (target.path !== location.pathname) {
        navigate(target.path, { replace: true });
      }
      return;
    }

    window.location.assign(target.url);
  }, [
    adminProductContext?.product,
    churchProducts,
    currentChurch?.churchId,
    currentChurch?.scopeTypes,
    isChurchProductsError,
    isChurchProductsPending,
    isCommunityListOnlyScope,
    isGroupLimitedScope,
    isLimitedScope,
    isOnHomePath,
    isSuccess,
    location.pathname,
    navigate,
  ]);

  return null;
};
